import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import {
  DesktopLicenseChangeLogEntry,
  DesktopLicenseChangeLogVariantSummary,
  DesktopLicenseFieldChange,
  DesktopLicenseWithProduct,
  LicensesService,
} from 'src/app/services/licenses.service';
import { Tenant } from 'src/app/services/tenants.service';

const CHANGE_SOURCE_LABELS: Record<string, string> = {
  api_update: 'Admin Update',
  renewal: 'Renewal',
  scheduled_lock_level: 'Scheduled Lock Level',
  bulk_lock_level: 'Bulk Lock Level',
  delete: 'Deleted',
};

const FIELD_LABELS: Record<string, string> = {
  active: 'Active',
  site_license: 'Site License',
  will_renew: 'Will Renew',
  new_license: 'New License',
  renewal: 'Renewal',
  transfers_left: 'Transfers Left',
  expiration: 'Expiration',
  start_date: 'Start Date',
  parent_desktop_license_id: 'Parent License',
  notes: 'Notes',
  deleted: 'Deleted',
};

@Component({
  selector: 'app-license-change-history',
  templateUrl: './license-change-history.component.html',
  styleUrls: ['./license-change-history.component.scss'],
})
export class LicenseChangeHistoryComponent {
  loadingLicense = false;
  loadingChangeLogs = false;
  errorFetchingLicense = '';
  errorFetchingChangeLogs = '';

  license?: DesktopLicenseWithProduct;
  tenant?: Tenant;
  changeLogs: DesktopLicenseChangeLogEntry[] = [];

  constructor(
    public location: Location,
    private route: ActivatedRoute,
    private licensesService: LicensesService
  ) {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.errorFetchingLicense = 'No license ID found';
      return;
    }

    this.getLicense(id);
    this.getChangeLogs(id);
  }

  getLicense(id: string) {
    this.loadingLicense = true;
    this.licensesService.getLicenseDetails(id).subscribe({
      next: (license) => {
        this.loadingLicense = false;
        this.license = license.desktop_license;
        this.tenant = license.tenant;
      },
      error: (err) => {
        this.loadingLicense = false;
        console.error(err);
        this.errorFetchingLicense = 'Error fetching license and tenant data';
      },
    });
  }

  getChangeLogs(id: string) {
    this.loadingChangeLogs = true;
    this.licensesService.getLicenseChangeLog(id).subscribe({
      next: (response) => {
        this.loadingChangeLogs = false;
        this.changeLogs = response.change_logs;
      },
      error: (err) => {
        this.loadingChangeLogs = false;
        console.error(err);
        this.errorFetchingChangeLogs = 'Error fetching license change history';
      },
    });
  }

  getLicenseType(license: DesktopLicenseWithProduct) {
    return this.licensesService.getType(license);
  }

  getLicensePrice(license: DesktopLicenseWithProduct) {
    return this.licensesService.getPrice(license);
  }

  getChangeSourceLabel(source: string) {
    return CHANGE_SOURCE_LABELS[source] ?? source;
  }

  getChangedByLabel(entry: DesktopLicenseChangeLogEntry) {
    if (!entry.changed_by_user) {
      return 'System';
    }
    return entry.changed_by_user.username || entry.changed_by_user.email;
  }

  formatVariantSummary(variant: DesktopLicenseChangeLogVariantSummary) {
    return `${variant.description} (lock level ${variant.price_lock_level})`;
  }

  formatFieldLabel(field: string) {
    return FIELD_LABELS[field] ?? field;
  }

  formatFieldValue(value: unknown) {
    if (value === null || value === undefined) {
      return 'None';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  }

  formatFieldChange(change: DesktopLicenseFieldChange) {
    return `${this.formatFieldLabel(change.field)}: ${this.formatFieldValue(change.old)} → ${this.formatFieldValue(change.new)}`;
  }

  getChangeSummaries(entry: DesktopLicenseChangeLogEntry) {
    const summaries: string[] = [];

    if (entry.old_product_variant && entry.new_product_variant) {
      summaries.push(
        `Product variant: ${this.formatVariantSummary(entry.old_product_variant)} → ${this.formatVariantSummary(entry.new_product_variant)}`
      );
    } else if (entry.new_product_variant) {
      summaries.push(
        `Product variant: ${this.formatVariantSummary(entry.new_product_variant)}`
      );
    } else if (entry.old_product_variant && entry.change_source === 'delete') {
      summaries.push(
        `Product variant: ${this.formatVariantSummary(entry.old_product_variant)}`
      );
    }

    for (const change of entry.field_changes ?? []) {
      summaries.push(this.formatFieldChange(change));
    }

    if (summaries.length === 0 && entry.reason) {
      summaries.push(entry.reason);
    }

    return summaries;
  }
}
